// Reusable install button + iOS-instructions sheet used on the marketing
// landing page. Renders nothing if the user already installed the PWA or
// dismissed the CTA recently.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, X, Share, Plus } from "lucide-react";
import { Button } from "./ui/button";
import { useInstallPrompt } from "../lib/pwaInstall";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

/** Standalone install button — renders the correct copy for the platform.
 *  If neither native prompt nor iOS is available (e.g. desktop Firefox) we
 *  still show the button but tapping it opens a generic instruction sheet. */
export function InstallButton({
  className = "",
  variant = "primary",
  label = "Install SpotDrop",
  testId = "install-cta",
}) {
  const install = useInstallPrompt();
  // Only hide when we KNOW the app is already installed / running standalone.
  // Never hide just because the button was clicked or a prompt was dismissed —
  // the user must be able to retry from any CTA on the page.
  if (install.isInstalled) return null;

  const styles =
    variant === "primary"
      ? "bg-[#ccff00] text-[#0a0a0c] hover:bg-[#b8e600] font-semibold"
      : "bg-white/[0.03] border border-white/15 text-white hover:bg-white/[0.06] font-medium";

  // `link` keeps the install option available without competing with the
  // Google Play badge for attention.
  if (variant === "link") {
    return (
      <>
        <button
          type="button"
          onClick={install.promptInstall}
          className={`inline-flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors font-medium ${className}`}
          data-testid={testId}
        >
          <Smartphone className="w-3.5 h-3.5" />
          {label}
        </button>
        <IOSInstallSheet
          open={install.showIOSInstructions}
          onClose={() => install.setShowIOSInstructions(false)}
        />
      </>
    );
  }

  return (
    <>
      <Button
        type="button"
        onClick={install.promptInstall}
        className={`h-12 rounded-full px-6 text-base ${styles} ${className}`}
        data-testid={testId}
      >
        <Smartphone className="w-4 h-4 mr-2" />
        {label}
      </Button>
      <IOSInstallSheet
        open={install.showIOSInstructions}
        onClose={() => install.setShowIOSInstructions(false)}
      />
    </>
  );
}

/** Modal-style bottom sheet with iOS Add-to-Home-Screen steps. */
export function IOSInstallSheet({ open, onClose }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1100] bg-black/70 backdrop-blur-sm"
            {...dismissProps}
            data-testid="ios-install-backdrop"
          />
          {/* Flex-centering wrapper — avoids framer-motion 'y' transform
              conflicting with Tailwind '-translate-x-1/2' offsets. */}
          <div className="fixed inset-0 z-[1101] flex items-end md:items-center justify-center pointer-events-none px-3 pb-4 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.25 }}
              className="pointer-events-auto w-full max-w-md p-6 rounded-3xl bg-[#141416] border border-white/10 shadow-2xl text-left"
              data-testid="ios-install-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Install SpotDrop on iPhone"
            >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  Install on iPhone
                </div>
                <div className="font-heading text-xl font-semibold mt-1">
                  Add SpotDrop to your home screen
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/5"
                data-testid="ios-install-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-zinc-200">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/40 text-[#ccff00] flex items-center justify-center text-xs font-semibold shrink-0">
                  1
                </span>
                <span className="leading-relaxed">
                  Open SpotDrop in <strong>Safari</strong> (the blue compass icon).
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/40 text-[#ccff00] flex items-center justify-center text-xs font-semibold shrink-0">
                  2
                </span>
                <span className="leading-relaxed">
                  Tap the <Share className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5 text-[#ccff00]" />{" "}
                  <strong>Share</strong> button at the bottom of the screen.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/40 text-[#ccff00] flex items-center justify-center text-xs font-semibold shrink-0">
                  3
                </span>
                <span className="leading-relaxed">
                  Scroll and tap{" "}
                  <span className="whitespace-nowrap">
                    <Plus className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5 text-[#ccff00]" />
                    <strong>Add to Home Screen</strong>
                  </span>
                  , then confirm.
                </span>
              </li>
            </ol>
            <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
              SpotDrop will appear on your home screen and open fullscreen — like a
              native app. No account required.
            </p>
            <Button
              type="button"
              onClick={onClose}
              className="w-full mt-5 h-11 bg-white/[0.04] border border-white/10 text-white hover:bg-white/[0.08] font-medium rounded-xl"
              data-testid="ios-install-got-it"
            >
              Got it
            </Button>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
